import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { ProfessionalStateService } from '../../services/professional-state.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {

  selectedProfessional: any = null;
  professionals: any[]      = [];
  loadingProfessionals      = true;

  stats = {
    todayTotal: 0, todayConfirmed: 0, todayPending: 0,
    weekTotal: 0, monthRevenue: 0, pendingPayments: 0, totalPatients: 0
  };
  todayAppointments:    any[] = [];
  upcomingAppointments: any[] = [];
  loadingStats = false;

  private subs = new Subscription();

  constructor(
    private profState: ProfessionalStateService,
    private api: ApiService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.profState.list$.subscribe(list => this.professionals = list)
    );
    this.subs.add(
      this.profState.loading$.subscribe(v => this.loadingProfessionals = v)
    );
    this.subs.add(
      this.profState.selected$.subscribe(p => {
        this.selectedProfessional = p;
        if (p) this.loadStats(p.id);
      })
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  requestModal(): void { this.profState.requestModal(); }

  loadStats(professionalId: number): void {
    this.loadingStats = true;
    this.stats = { todayTotal: 0, todayConfirmed: 0, todayPending: 0,
                   weekTotal: 0, monthRevenue: 0, pendingPayments: 0, totalPatients: 0 };
    this.todayAppointments    = [];
    this.upcomingAppointments = [];

    const today      = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow   = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const weekEnd    = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd   = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);

    // Agendamentos de hoje
    this.api.getAppointments(professionalId, {
      startDate: today.toISOString(), endDate: tomorrow.toISOString(), pageSize: 500
    }).subscribe({
      next: r => {
        this.todayAppointments    = r.items;
        this.stats.todayTotal     = r.items.length;
        this.stats.todayConfirmed = r.items.filter((a: any) => a.status === 2).length;
        this.stats.todayPending   = r.items.filter((a: any) => a.status === 1).length;
      },
      error: () => {}
    });

    // Próximos 7 dias
    this.api.getAppointments(professionalId, {
      startDate: tomorrow.toISOString(), endDate: weekEnd.toISOString(), pageSize: 500
    }).subscribe({
      next: r => {
        this.upcomingAppointments = r.items.slice(0, 5);
        this.stats.weekTotal      = r.totalCount;
        this.loadingStats         = false;
      },
      error: () => { this.loadingStats = false; }
    });

    // Resumo financeiro do mês
    this.api.getPaymentSummary(
      professionalId,
      monthStart.toISOString(),
      monthEnd.toISOString()
    ).subscribe({
      next: s => {
        this.stats.monthRevenue    = s.totalReceived ?? s.totalAmount ?? 0;
        this.stats.pendingPayments = s.totalPending  ?? 0;
      },
      error: () => {}
    });

    // Total de pacientes ativos
    this.api.getPatients(professionalId, { pageSize: 1, isActive: true }).subscribe({
      next: r => { this.stats.totalPatients = r.totalCount; },
      error: () => {}
    });
  }

  navigate(r: string): void { this.router.navigate([r]); }

  greeting(): string {
    const h = new Date().getHours();
    return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
  }

  daysRemaining(date?: string): number {
    if (!date) return 0;
    return Math.max(0, Math.ceil((new Date(date).getTime() - Date.now()) / 86400000));
  }

  subLabel(status?: number): string {
    const m: any = { 1: '🆓 Trial', 2: '✅ Ativo', 3: '⚠️ Atrasado', 4: '🚫 Suspenso', 5: 'Cancelado' };
    return m[status!] || '';
  }

  subClass(status?: number): string {
    const m: any = { 1: 'bg-info', 2: 'bg-success', 3: 'bg-warning text-dark', 4: 'bg-danger', 5: 'bg-secondary' };
    return `badge ${m[status!] || 'bg-secondary'}`;
  }

  apptStatusLabel(s: number): string {
    return ['', 'Agendado', 'Confirmado', 'Realizado', 'Cancelado'][s] || '-';
  }

  apptStatusClass(s: number): string {
    const m: any = { 1: 'bg-warning text-dark', 2: 'bg-success', 3: 'bg-info', 4: 'bg-danger' };
    return `badge ${m[s] || 'bg-secondary'}`;
  }

  fmtTime(d: string): string {
    return new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  fmtDate(d: string): string {
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }

  fmtCurrency(v: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
  }

  initials(name: string): string {
    return (name || '?').substring(0, 2).toUpperCase();
  }
}
